// 本地路由导入
import dynamicRoutes from '@/router/dynamic'
import jxicDynamicRoutes from '@/router/jxic-router/index'

// 数据字典
import { menuTypeDict } from '@/dictionary/menu'
import {
  platformFunctionModuleDict,
  workbenchServiceDict
} from '@/dictionary/system'
// 工具函数
import { transListToTree } from '@/utils/company'
import { cloneDeep, isEmpty, uniq, uniqBy, isNumber } from 'lodash'

let localMenuId = 0

/**
 * TODO: 专属于省投的路由过滤函数
 */
function filterJXICRouter(route, companyInfo) {
  return route.meta.title === '协同项目分析' && companyInfo.companyId !== 472
}

/**
 * 将定义的路由转化为vue-router可以使用的路由结构
 * @param serverRoute
 * @param route
 * @param parent
 * @param sort
 * @param companyType
 * @param platformAlias
 * @param isJxic
 * @returns {{path: (*|string), component: string, meta: any, name: string, icon: (*|string), sort: (*|number), id: (string|`local-${number}`), parentId: number}}
 */
function transDefinedToRouter({
  serverRoute = {},
  route,
  parent = {},
  sort,
  companyType,
  platformAlias,
  isJxic
}) {
  // ------- 通过配置得到当前的菜单名称 -------
  // 主体类型 对应的 菜单名称
  const companyTypeObj = (
    serverRoute.applicableCompanyTypes || route.applicableCompanyTypes
  ).find(type => type.companyType === companyType)
  // 平台对应的菜单别称
  const platformAliasObj = (
    serverRoute.platformAlias || route.platformAlias
  ).find(alias => alias.platformId === platformAlias)

  const currentRouteTitle =
    platformAliasObj?.menuName ||
    companyTypeObj?.menuName ||
    serverRoute?.menuInfo?.name ||
    route?.meta?.title ||
    ''

  // ------- 生成通用的路由配置 -------
  const routeId = route.menuId || serverRoute.menuId || `local-${localMenuId++}`
  return {
    path:
      route?.meta?.isExternal && route.path.startsWith(parent.path || '/')
        ? route.path
        : `/${parent.path || '/'}/${route.path}`.replace(/\/+/g, '/'),
    name: route.name || '',
    component: route.component || '',
    sort:
      isNumber(serverRoute?.menuInfo?.sort) && !isJxic
        ? serverRoute?.menuInfo?.sort
        : sort || 0,
    meta: Object.assign({}, route.meta, {
      menuId: routeId,
      title: currentRouteTitle,
      layout: route?.meta?.layout || parent?.meta?.layout || 'DefaultLayout',
      applicablePlatform:
        (isJxic
          ? route.applicableCompanyIdentities
          : serverRoute.applicableCompanyIdentities) ||
        route.applicableCompanyIdentities ||
        [],
      breadcrumb: [
        ...(parent?.meta?.breadcrumb || []),
        {
          title: currentRouteTitle,
          name: route.name,
          id: routeId
        }
      ]
    }),
    id: routeId,
    parentId: parent.id || 0,
    activeIds: [routeId],
    icon: route?.meta?.icon || '',
    isServer: !!serverRoute?.routeInfo?.path,
    type: route.type,
    roles: route.roles || []
  }
}

/**
 * 通过后端返回的路由配置，重新生成并过滤本地的路由，返回一维路由配置
 * @param serverRouteObj  服务器端返回的一维路由列表
 * @param localRouteList  前端本地定义的路由列表
 * @param companyInfo -companyType 当前登录的公司类型（企业、事业单位、个体工商户、自然人）
 * @param platformAlias  当前选择的平台（采购人、代理机构、供应商）
 * @param roles  当前登录人所拥有的角色
 */
function filterRouterByServerData(
  serverRouteObj,
  localRouteList,
  companyInfo,
  platformAlias,
  roles
) {
  // 记录用户没有权限的路由id
  const noRightRouteList = []
  // 一维的路由列表，将本地定义的路由转化为vue-router定义后，拍平后的路由列表
  const routeListTemp = []

  // 递归函数，将本地定义的路由与服务端的定义相结合，并转化为vue-router格式
  // 并过滤掉服务端没有返回的大类
  // hidden为false的默认为服务端需要分配的路由
  const loop = (list, serverObj, parent = {}) => {
    list.map((route, index) => {
      const serverRoute = serverObj[route.path] || {}

      // 若路由hidden为false（说明为服务端路由）若服务端没有返回，则说明当前平台没有分配该路由，进行过滤
      // TODO: 省投使用自定义路由，不因为后端不返回的配置而做过滤
      if (
        !companyInfo.isJxic &&
        !route.meta.hidden &&
        !(route.path in serverObj)
      ) {
        return
      }

      // TODO: 省投自己的过滤函数
      if (companyInfo.isJxic && filterJXICRouter(route, companyInfo)) {
        return
      }

      // 生成路由
      const routeTemp = transDefinedToRouter({
        serverRoute: serverRoute.route || {},
        route,
        parent,
        sort: index,
        companyType: companyInfo.companyType,
        platformAlias,
        isJxic: companyInfo.isJxic
      })
      // 菜单类型的路由，引入页面组件
      if (route.type === menuTypeDict.MENU.value) {
        try {
          routeTemp.component = route.component
            ? resolve => require([`@/views${route.component}`], resolve)
            : null
        } catch (e) {
          //
        }
      }

      // 对每一个路由完善activeIds，用于高亮侧边路由
      if ('activeIds' in parent) {
        parent.activeIds.push(routeTemp.id)
      }
      if (route.children && route.children.length) {
        routeTemp.name = route.children[0]?.name || ''
        loop(route.children, serverRoute, routeTemp)
        parent.activeIds = uniq([
          ...(parent.activeIds || []),
          ...routeTemp.activeIds
        ])
      }

      // 若用户没有该路由的权限，则将路由的id存入到noRightRouteIdList的数组中，并进行过滤
      // 这里必须等到有路由id生成之后
      if (!roles?.filter(role => route.roles.includes(role))?.length) {
        noRightRouteList.push(routeTemp)
        return
      }

      routeListTemp.push(routeTemp)
    })
  }

  // 返回拍平后的数组
  loop(localRouteList, serverRouteObj, {})
  return {
    routeList: uniqBy(routeListTemp, 'id'),
    noRightRouteList: noRightRouteList.filter(v => v)
  }
}

export default {
  namespaced: true,

  state: {
    // 缓存路由名称列表
    cachedViews: [],

    // 侧边菜单路由列表
    sideMenuList: [],
    // 平台401的路由id列表
    noRightRouteIdList: [],
    // 平台系统的角色权限列表
    systemRolesList: [],
    // 系统工作台列表：采购人、代理机构、供应商 [1, 2, 3]
    systemWorkbenchList: [],
    // 当前工作台
    currentWorkbench: '',
    // 当前的一级路由
    currentFirstLevelRouteId: 0 // 为0为空则未选中
  },

  getters: {
    // 工作台对应的路由列表
    workbenchMenuList(state) {
      const loop = (menus = []) => {
        return menus
          .map(menu => {
            if (menu.children && menu.children.length) {
              menu.children = loop(menu.children)
            }

            if (
              menu?.meta?.applicablePlatform?.includes(
                state.currentWorkbench
              ) &&
              !menu?.meta?.hidden
            ) {
              return menu
            } else {
              return undefined
            }
          })
          .filter(value => value)
      }

      return loop(cloneDeep(state.sideMenuList)).filter(
        value => !isEmpty(value.children)
      )
    },

    // 一级路由对应的下级路由列表
    expandMenuList(state, getters) {
      return (
        getters.workbenchMenuList.filter(
          menu => menu.id === state.currentFirstLevelRouteId
        )[0]?.children || []
      )
    }
  },

  mutations: {
    // 将路由对应的页面加入页面缓存
    ADD_CACHED_VIEW(state, route) {
      // pageComponentName 若存在共用的页面组件时，需要同时route.name 和 页面的pageName-
      if (!route.name || !route?.meta?.cache) return

      if (!state.cachedViews.includes(route.name)) {
        state.cachedViews.push(route.name)
      }
      if (
        route?.meta?.pageComponentName &&
        !state.cachedViews.includes(route.meta.pageComponentName)
      ) {
        state.cachedViews.push(route.meta.pageComponentName)
      }
    },

    // 将路由对应的页面去除页面缓存
    REMOVE_CACHE_VIEW(state, route) {
      if (!route.name) return

      const index = state.cachedViews.indexOf(route.name)
      index > -1 && state.cachedViews.splice(index, 1)

      if (route?.meta?.pageComponentName) {
        const tempIndex = state.cachedViews.indexOf(
          route.meta.pageComponentName
        )
        tempIndex > -1 && state.cachedViews.splice(tempIndex, 1)
      }
    },

    // 清空缓存页面
    CLEAR_CACHE_VIEW(state) {
      state.cachedViews = []
    },

    // 设置侧边菜单列表
    SET_SIDE_MENUS(state, menus) {
      state.sideMenuList = menus
    },

    // 设置当前平台的401路由id列表
    SET_NO_RIGHT_MENUS_ID_LIST(state, menusIdList) {
      state.noRightRouteIdList = menusIdList || []
    },

    // 设置系统的角色权限列表
    SET_SYSTEM_ROLES_LIST(state, routeList) {
      let systemRolesList = []

      // 设置系统的角色权限列表
      routeList.forEach(route => {
        systemRolesList = uniq([
          ...systemRolesList,
          ...(route.type === menuTypeDict.MENU.value ? route.roles : [])
        ])
      })
      state.systemRolesList = systemRolesList
    },

    /**
     * 设置系统平台列表
     */
    SET_SYSTEM_PLATFORM_LIST(state, { roles, companyInfo }) {
      let systemWorkbenchList = []
      platformFunctionModuleDict.array.forEach(dict => {
        if (roles.includes(dict.right)) {
          systemWorkbenchList.push(...dict.service)
        }
      })
      systemWorkbenchList = uniq(systemWorkbenchList).filter(platform => {
        return companyInfo[
          workbenchServiceDict.translate(platform).certification
        ]
      })
      state.systemWorkbenchList = systemWorkbenchList
    },

    /**
     * 设置当前的工作台
     */
    SET_CURRENT_WORKBENCH(state, currentWorkbench) {
      state.currentWorkbench = currentWorkbench || 0
    },

    // 设置当前选中的一级路由
    SET_FIRST_LEVEL_ROUTE(state, routeId) {
      state.currentFirstLevelRouteId = routeId || 0
    }
  },

  actions: {
    /**
     * 获取路由列表
     * @param commit
     * @param dispatch
     * @param menus
     * @param companyInfo
     * @param platformAlias
     * @param roles
     * @returns {Promise<unknown>}
     */
    getRouterList(
      { commit, dispatch },
      { menus, companyInfo, platformAlias, roles }
    ) {
      return new Promise(resolve => {
        // 将服务端返回的路由转化为树形结构
        const serverRouteObj = {}
        const serverRouteList = transListToTree(menus, 'menuId')
        const loop = (list, obj = {}) => {
          list.forEach(item => {
            // TODO：等运营平台删除叶子节点后，去除menu?.meta?.hidden的条件
            if (item?.routeInfo?.meta?.hidden) {
              return
            }
            obj[item?.routeInfo?.path] = {
              route: item
            }

            if (item.children && item.children.length) {
              loop(item.children, obj[item?.routeInfo?.path])
            }
          })
        }
        loop(serverRouteList, serverRouteObj)

        const localRoutes = companyInfo.isJxic
          ? jxicDynamicRoutes
          : dynamicRoutes

        // 通过运营平台分配的路由及用户权限，过滤并根据本地路由生成定义路由，并直接对本地定义的路由进行拍平处理
        // 返回的routerList包含目录类型（没有绑定页面组件的路由）、菜单类型的路由（实际绑定了页面组件的路由）
        const { routeList, noRightRouteList } = filterRouterByServerData(
          serverRouteObj,
          localRoutes,
          companyInfo,
          platformAlias,
          roles
        )

        // 根据返回的路由，设置当前平台的权限列表，和当前的系统平台列表
        commit('SET_SYSTEM_ROLES_LIST', [...routeList, ...noRightRouteList])
        commit('SET_SYSTEM_PLATFORM_LIST', { roles, companyInfo })

        // 侧边路由均在后端后定义，所以这里只需要过滤出特定的路由传入即可
        // TODO：等运营平台删除叶子节点后，去除menu?.meta?.hidden的条件
        commit(
          'SET_SIDE_MENUS',
          transListToTree(
            cloneDeep(
              routeList
                .filter(route => !route?.meta?.hidden)
                .sort((a, b) => a.sort - b.sort)
            ).map(route => {
              delete route.component
              return route
            }),
            'id'
          )
        )

        commit(
          'SET_NO_RIGHT_MENUS_ID_LIST',
          noRightRouteList.map(route => route.name)
        )
        // 过滤掉目录类型的路由数据，只有菜单类型的路由数据真正做跳转
        resolve(
          routeList.filter(route => route.type === menuTypeDict.MENU.value)
        )
      })
    },

    /**
     * 设置当前工作台
     * @param state
     * @param commit
     * @param route
     * @returns {Promise<unknown>}
     */
    setCurrentWorkbench({ state, commit }, route) {
      return new Promise(resolve => {
        let storage = Number(localStorage.getItem('currentWorkbench') || 0)
        const applicablePlatform =
          typeof route === 'number'
            ? [route]
            : route?.meta?.applicablePlatform || [storage]

        if (
          storage &&
          state.systemWorkbenchList.includes(storage) &&
          applicablePlatform.includes(storage)
        ) {
          commit('SET_CURRENT_WORKBENCH', storage)
          localStorage.setItem('currentWorkbench', storage)
        } else {
          storage = 0
        }

        if (!applicablePlatform.length) return resolve()

        if (!storage || !applicablePlatform.includes(storage)) {
          const currentDict =
            workbenchServiceDict.array.find(dict => {
              if (
                applicablePlatform.includes(dict.value) &&
                state.systemWorkbenchList.includes(dict.value)
              ) {
                return true
              }
            }) || {}

          commit('SET_CURRENT_WORKBENCH', currentDict.value)
          localStorage.setItem('currentWorkbench', currentDict.value)
        }

        resolve()
      })
    }
  }
}
